
import { mapState, mapGetters } from 'vuex';
import * as XLSX from 'xlsx';

export default {
  // eslint-disable-next-line vue/require-prop-types
  props: ['zone', 'dmg'],

  data() {
    return {
      activeTab: 1,
      tabs: [{ label: this.$store.state.user.translate.value }, { label: '%' }],
      rows: [],
      rowsB: [],
      columns: [], // raw columns
      columnSettings: [] // reactive toggle columns
    };
  },

  computed: {
    ...mapState('store', ['stores']),
    ...mapGetters('filter', [
      'getStartingPeriod',
      'getEndingPeriod',
      'getStoreCodesList'
    ]),

    // Filter columns based on toggle
    visibleColumns() {
      return this.columnSettings.filter(c => c.visible);
    }
  },

  watch: {
    zone() {
      this.addZoneMethod();
    }
  },

  methods: {
    methodChange(index) {
      this.activeTab = index;
    },

    toggleExpand(row) {
      row.expand = !row.expand;
      this.rows = [...this.rows];
    },

    addAndRemovePercentageSymbols(data, add = this.activeTab) {
      return data?.map(zone => {
        const sumValues = obj =>
          Object.values(obj)
            .filter(value => typeof value === 'number')
            .reduce((acc, num) => acc + num, 0);

        const p = sumValues(zone) || 1;

        return {
          ...zone,
          ...Object.fromEntries(
            Object.entries(zone).map(([key, value]) => {
              if (typeof value === 'number')
                return [
                  key,
                  add ? `${((value / p) * 100).toFixed(2)}%` : value
                ];
              return [key, value];
            })
          ),
          subZones:
            zone.subZones?.map(subZone => {
              const sp = sumValues(subZone) || 1;
              return {
                ...subZone,
                ...Object.fromEntries(
                  Object.entries(subZone).map(([key, value]) => {
                    if (typeof value === 'number')
                      return [
                        key,
                        add ? `${((value / sp) * 100).toFixed(2)}%` : value
                      ];
                    return [key, value];
                  })
                )
              };
            }) || []
        };
      });
    },

    addZoneMethod() {
      const { stores } = this.stores;
      const tenantId = this.$store.state.user.profile?.tenant?._id;

      // --- Filter gender fields by existing labels ---
      const genderFields = [
        { field: 'male_18', key: 'male_child' },
        { field: 'male_19_44', key: 'male_young' },
        { field: 'male_45_59', key: 'male_adult' },
        { field: 'male_60', key: 'male_senior' },
        { field: 'female_18', key: 'female_child' },
        { field: 'female_19_44', key: 'female_young' },
        { field: 'female_45_59', key: 'female_adult' },
        { field: 'female_60', key: 'female_senior' }
      ];

      // Keep only fields with a valid label
      const validFields = genderFields.filter(f => {
        const label = this.dmg
          ? this.$store.state.user.translate[`${f.key}_${tenantId}`] || ''
          : this.$store.state.user.translate[f.key];
        return label && label.trim() !== '';
      });

      // --- Build rows ---
      const rows = Object.keys(this.zone).map(key => {
        const { summary, zones } = this.zone[key];
        const findName = stores?.find(name => name.storeCode == key)?.name;

        // Only include dm values for valid fields
        const filteredDm = {};
        validFields.forEach((f, index) => {
          filteredDm[f.field] = summary.dm[index + 1]; // dm keys are 1-based
        });

        return {
          name: findName ? `${key} - ${findName}` : key,
          ...filteredDm,
          expand: false,
          subZones: Object.keys(zones || {}).map(k => {
            const zon = zones[k];
            const filteredSubDm = {};
            validFields.forEach((f, index) => {
              filteredSubDm[f.field] = zon.dm[index + 1];
            });
            return {
              name: findName ? `${findName} ${k}` : k,
              ...filteredSubDm
            };
          })
        };
      });

      this.rows = rows;
      this.rowsB = this.addAndRemovePercentageSymbols(rows);

      // --- Build columnSettings based on valid labels ---
      const defaultColumns = [
        {
          name: 'name',
          label: this.$store.state.user.translate.name,
          visible: true,
          field: 'name',
          align: 'left',
          sortable: true
        }
      ];

      validFields.forEach(f => {
        const label = this.dmg
          ? this.$store.state.user.translate[`${f.key}_${tenantId}`] || ''
          : this.$store.state.user.translate[f.key];
        defaultColumns.push({
          name: f.field,
          label,
          visible: true,
          field: f.field,
          align: 'center',
          sortable: true
        });
      });

      this.columnSettings = defaultColumns;
    },

    // Export CSV respecting visible columns
    exportToCSV() {
      const rows = this.activeTab ? this.rowsB : this.rows;
      const cols = this.visibleColumns;

      let dataToExport = [];

      rows.forEach(row => {
        let rowData = {};
        cols.forEach(col => {
          if (row.hasOwnProperty(col.name))
            rowData[col.label] = row[col.name] || '-';
        });
        dataToExport.push(rowData);

        row.subZones?.forEach(subZone => {
          let subZoneData = {};
          cols.forEach(col => {
            if (subZone.hasOwnProperty(col.name))
              subZoneData[col.label] = subZone[col.name] || '-';
          });
          dataToExport.push(subZoneData);
        });
      });

      const csvContent = [
        cols.map(col => col.label).join(','),
        ...dataToExport.map(row =>
          Object.values(row)
            .map(v => `"${v}"`)
            .join(',')
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${this.$store.state.user.translate.gender_age_overview}_${this.getStartingPeriod}_${this.getEndingPeriod}.csv`;
      document.body.appendChild(link);
      link.click();
    },

    // Export Excel respecting visible columns
    exportToExcel() {
      const rows = this.activeTab ? this.rowsB : this.rows;
      const cols = this.visibleColumns;

      let dataToExport = [];

      rows.forEach(row => {
        let rowData = {};
        cols.forEach(col => {
          if (row.hasOwnProperty(col.name))
            rowData[col.label] = row[col.name] || '-';
        });
        dataToExport.push(rowData);

        row.subZones?.forEach(subZone => {
          let subZoneData = {};
          cols.forEach(col => {
            if (subZone.hasOwnProperty(col.name))
              subZoneData[col.label] = subZone[col.name] || '-';
          });
          dataToExport.push(subZoneData);
        });
      });

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Zone Data');
      XLSX.writeFile(
        workbook,
        `${this.$store.state.user.translate.gender_age_overview}_${this.getStartingPeriod}_${this.getEndingPeriod}.xlsx`
      );
    }
  }
};
